import { CSSProperties, Ref, useEffect } from 'react'

import { useLexicalEditable } from '@lexical/react/useLexicalEditable'
import { useClickAway } from 'ahooks'
import { createStyles } from 'antd-style'
import { isNil } from 'lodash'

import { useMergedRef } from '@eastcoal/common/hooks/useMergedRef'
import { pick } from '@eastcoal/common/utils/object'

import { useRichEditorStore } from '../../../Store/hooks/useStore'
import { ImageCardImg } from '../Img'
import { ImageResizer } from '../Resizer'
import { useImageCardStore } from '../Store/hooks/useStore'
import { toPercentage } from '../utils/size'
import { IMAGE_BORDER_STYLE, IMAGE_SHADOW_STYLE } from '../utils/style'

const useStyles = createStyles(({ token }) => ({
  imageBox: {
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'bottom',
    borderRadius: token.borderRadiusOuter,
    [`&::before`]: {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: token.borderRadiusOuter
    },
    [`&.is-bordered::before`]: {
      border: IMAGE_BORDER_STYLE
    },
    [`&.is-shadowed::before`]: {
      boxShadow: IMAGE_SHADOW_STYLE
    },
    [`&.is-actived`]: {
      border: `1px solid ${token.colorPrimaryBorderHover}`
    }
  },
  imageResizer: {
    display: 'none',
    [`&.is-actived`]: {
      display: 'block'
    }
  },
  imageTransformer: {
    position: 'relative'
  },
  image: {
    maxWidth: '100%',
    borderRadius: token.borderRadiusOuter
  }
}))

export const ImageBox = ({ ref }: { ref?: Ref<HTMLDivElement> }) => {
  const editable = useLexicalEditable()
  const previewFileRef = useRichEditorStore(state => state.previewFileRef)
  const {
    containerRef,
    ossId,
    ossUrl,
    filename,
    active,
    rotate,
    bordered,
    shadowed,
    currentWidth,
    currentHeight,
    toggleBordered,
    toggleShadowed,
    isReverseSize,
    toggleActive,
    hasLink,
    openLink
  } = useImageCardStore(state =>
    pick(state, [
      'containerRef',
      'ossId',
      'ossUrl',
      'filename',
      'active',
      'loading',
      'rotate',
      'bordered',
      'shadowed',
      'currentWidth',
      'currentHeight',
      'setAlign',
      'toggleBordered',
      'toggleShadowed',
      'isReverseSize',
      'toggleActive',
      'hasLink',
      'openLink'
    ])
  )

  useEffect(() => {
    const imageBox = containerRef.current
    if (imageBox) {
      toggleBordered(imageBox.classList.contains('is-bordered'))
      toggleShadowed(imageBox.classList.contains('is-shadowed'))
    }
  }, [containerRef, toggleBordered, toggleShadowed])

  const rotated = !isNil(rotate)
  let transformerStyle: CSSProperties | undefined = undefined
  let imageStyle: CSSProperties | undefined = undefined

  if (rotated) {
    const reverse = isReverseSize()
    const width = reverse ? currentHeight : currentWidth
    const height = reverse ? currentWidth : currentHeight
    const paddingBlockEnd = toPercentage(height / width)
    transformerStyle = {
      width,
      paddingBlockEnd
    }
    const scaleWidth = reverse ? currentWidth / currentHeight : 1
    const scaleHeight = reverse ? currentHeight / currentWidth : 1
    const translateX = toPercentage((scaleHeight - 1) / 2)
    const translateY = toPercentage((scaleWidth - 1) / 2)

    imageStyle = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: toPercentage(scaleWidth),
      maxWidth: toPercentage(scaleWidth),
      height: toPercentage(scaleHeight),
      transformOrigin: '50% 50%',
      transform: `translate(${translateX}, ${translateY}) rotate(${rotate}deg)`
    }
  }

  useClickAway(
    () => toggleActive(false),
    [containerRef],
    ['click', 'pointerdown']
  )

  const handleClickContainer = () => {
    if (editable) {
      toggleActive(true)
    } else if (hasLink()) {
      openLink()
    } else {
      previewFileRef.current?.open({
        ossId,
        url: ossUrl,
        name: filename,
        type: 'image/*'
      })
    }
  }

  const mergedRef = useMergedRef(ref, containerRef)

  const { cx, styles } = useStyles()

  return (
    <div
      ref={mergedRef}
      role='button'
      tabIndex={-1}
      className={cx(styles.imageBox, {
        ['is-actived']: active,
        ['is-bordered']: bordered,
        ['is-shadowed']: shadowed
      })}
      onClick={handleClickContainer}
    >
      {editable && (
        <ImageResizer
          className={cx(styles.imageResizer, {
            'is-actived': active
          })}
        />
      )}
      <div className={styles.imageTransformer} style={transformerStyle}>
        <ImageCardImg
          alt={filename}
          className={styles.image}
          src={ossUrl}
          style={imageStyle}
          width={rotated ? undefined : currentWidth}
        />
      </div>
    </div>
  )
}
